/**
 * Activity's **New ▾** — `docs/activity.md` §9.1, Board 1 A.
 *
 * Folder 48's split look: the green half makes the tab's own thing (Calendar
 * → New meeting, Inbox → New text, Tasks → New task, All activity → New
 * note); the chevron opens the menu (392, Board 1 A) of every kind, with
 * *New request* under a rule. The tab's own row is the tinted one.
 */
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ActivityTab } from '@rowted/shared'
import { Button, buttonClass } from '../components/primitives.js'
import { Popover } from '../components/overlays.js'
import { PlusGlyph } from '../components/keycap.js'
import { MenuRow, NEW_REQUEST_KEYS, NEW_REQUEST_SUB } from '../requests/create-rows.js'
import { Glyph, type GlyphName } from './glyphs.js'

export type NewKind = 'text' | 'email' | 'task' | 'meeting' | 'note' | 'voice' | 'request'

export const NEW_ROWS: { kind: Exclude<NewKind, 'voice' | 'request'>; title: string; sub: string; glyph: GlyphName }[] = [
  { kind: 'text', title: 'Text', sub: 'to a contact, attached to their records', glyph: 'bubble' },
  { kind: 'email', title: 'Email', sub: 'from you, replies come back here', glyph: 'envelope' },
  { kind: 'task', title: 'Task', sub: 'with a due date, for one person or several', glyph: 'check_square' },
  { kind: 'meeting', title: 'Meeting', sub: 'on calendars — with a customer, or just the team', glyph: 'calendar' },
  { kind: 'note', title: 'Note', sub: 'on a contact, property or job', glyph: 'pencil' },
]

/** The green half's thing, per tab (§9.1). */
export const TAB_NEW: Record<ActivityTab, Exclude<NewKind, 'voice' | 'request'>> = {
  calendar: 'meeting', inbox: 'text', tasks: 'task', all: 'note',
}

export function NewButton({
  tab, onNew, canText, canRequest, size = 36,
}: {
  tab: ActivityTab
  onNew: (kind: NewKind) => void
  /** No Inbox, no Text or Email (§19 B18, T2). */
  canText: boolean
  canRequest: boolean
  size?: 36 | 40
}) {
  const chevron = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const rows = NEW_ROWS.filter((row) => canText || (row.kind !== 'text' && row.kind !== 'email'))
  const own = rows.some((row) => row.kind === TAB_NEW[tab]) ? TAB_NEW[tab] : 'task'
  const choose = (kind: NewKind) => { setOpen(false); onNew(kind) }

  return (
    <span className="inline-flex shrink-0 items-stretch">
      {/* The height inline: `cn` joins, and the toolbar size's own h-9 outranks an added h-10 (§10.1: 40 on an iPad). */}
      <Button variant="primary" size="toolbar" onClick={() => onNew(own)}
        className="rounded-r-none px-[13px]" style={{ height: size }}>
        <PlusGlyph size={14} stroke={2.2} />New
      </Button>
      <button
        type="button"
        aria-label="More to make"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={buttonClass({
          variant: 'primary',
          size: 'toolbar',
          /* Board 1 A: the chevron keeps its green while the menu is open. */
          className: 'relative w-8 rounded-l-none px-0',
        })}
        style={{ height: size }}
      >
        <span aria-hidden className="absolute -inset-y-px -left-px w-px bg-white/35" />
        {/* Board 1 A: the menu's right edge 6 px past the button's, 12 px under it. */}
        <span ref={chevron} aria-hidden className="absolute -right-1.5 left-0 top-[calc(100%+12px)] h-0" />
        <Glyph name="chevronDown" size={14} stroke={2.2} />
      </button>
      {open && createPortal(
        <Popover open onClose={() => setOpen(false)} anchor={chevron.current} align="right" width={392}
          label="New" className="rounded-xl py-1">
          <div role="menu" aria-label="New" className="flex flex-col">
            {/* Board 1 A tints the first row, where the keyboard starts. */}
            {rows.map((row, index) => (
              <MenuRow key={row.kind} feature={index === 0} title={row.title} sub={row.sub}
                icon={<Glyph name={row.glyph} size={17} />} onClick={() => choose(row.kind)} />
            ))}
            {canRequest && (
              <>
                <div className="my-1 h-px bg-line" />
                <MenuRow title="New request" sub={NEW_REQUEST_SUB} keys={NEW_REQUEST_KEYS}
                  icon={<Glyph name="plus" size={17} />} onClick={() => choose('request')} />
              </>
            )}
          </div>
        </Popover>,
        document.body,
      )}
    </span>
  )
}
